import { getSupabase } from "../supabase";

import {
  sessionsInWindow,
  lastLoginAt,
  activeUsersInWindow,
  dailySessionCounts,
  topByPayloadField,
  lastLoginPerUser,
  trendDirection,
} from "./usageStats.mjs";
import type { DashboardEventRow, DailyCount, TopEntry } from "./usageStats.mjs";
import { listTenantUsers } from "./tenantUsers.mjs";
import type { TenantUserRow } from "./tenantUsers.mjs";

/**
 * MER-211: data layer for /admin/usage. Reads `dashboard_event`
 * directly (staff-readable since migration 0040) and pushes all the
 * counting/grouping logic into usageStats.mjs so it stays unit-testable.
 *
 * v1 window is a fixed 60 days per customer (30 for the current
 * period + 30 for the trend comparison), matching the ticket's fixed
 * 30-day default; a date-range picker is an explicit v2 (out of
 * scope).
 */

const WINDOW_DAYS = 30;
const DETAIL_WINDOW_DAYS = 30;

export interface UsageSummaryRow {
  tenantId: string;
  sessionsLast30d: number;
  sessionsTrend: "up" | "down" | "flat";
  lastLoginAt: string | null;
  activeUsersLast7d: number;
  sparkline: DailyCount[];
}

export interface UsageDetail {
  dailySessions: DailyCount[];
  topTabs: TopEntry[];
  topWidgetDrills: TopEntry[];
  lastLoginPerUser: Array<{ userId: string; email: string | null; lastLoginAt: string }>;
}

async function fetchEventsSince(tenantId: string, days: number): Promise<DashboardEventRow[]> {
  const supabase = getSupabase();
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from("dashboard_event")
    .select("event_type, user_id, occurred_at, payload")
    .eq("tenant_id", tenantId)
    .gte("occurred_at", since)
    .order("occurred_at", { ascending: false })
    .limit(10000);
  if (error) throw new Error(`fetchEventsSince(${tenantId}) failed: ${error.message}`);
  return (data as DashboardEventRow[] | null) ?? [];
}

/** One summary row per tenant, for the /admin/usage landing table. */
export async function getUsageSummary(tenantId: string): Promise<UsageSummaryRow> {
  // 60 days covers the current 30-day window plus the prior 30 for trend.
  const events = await fetchEventsSince(tenantId, WINDOW_DAYS * 2);
  const current = sessionsInWindow(events, WINDOW_DAYS);
  const prior = sessionsInWindow(events, WINDOW_DAYS * 2) - current; // events older than 30d but within 60d
  return {
    tenantId,
    sessionsLast30d: current,
    sessionsTrend: trendDirection(current, prior),
    lastLoginAt: lastLoginAt(events),
    activeUsersLast7d: activeUsersInWindow(events, 7),
    sparkline: dailySessionCounts(events, WINDOW_DAYS),
  };
}

/** Full drill-down for one tenant's expanded row. */
export async function getUsageDetail(tenantId: string): Promise<UsageDetail> {
  const supabase = getSupabase();
  const [events, users] = await Promise.all([
    fetchEventsSince(tenantId, DETAIL_WINDOW_DAYS),
    listTenantUsers(supabase, tenantId).catch((): TenantUserRow[] => []),
  ]);
  const emailByUserId = new Map<string, string | null>(users.map((u) => [u.userId, u.email]));
  return {
    dailySessions: dailySessionCounts(events, DETAIL_WINDOW_DAYS),
    topTabs: topByPayloadField(events, "tab_change", "tab_name", 10),
    topWidgetDrills: topByPayloadField(events, "widget_drill", "widget_name", 10),
    lastLoginPerUser: lastLoginPerUser(events).map((row) => ({
      ...row,
      email: emailByUserId.get(row.userId) ?? null,
    })),
  };
}
